/**
 * The ONE post-login intent carrier: what the user asked for immediately
 * before being sent through sign-in, parked so the landing can replay it once
 * the session lands. One storage key, one JSON value — the modal intents
 * replay through the canonical team-modal controller (`openExisting`/
 * `openNew`); the checkout intent resumes the plan checkout. Every park goes
 * through `requestAuthForPostLoginIntent`, which raises the gate only once the
 * record is down and arms that gate's dismissal release: an abandoned request
 * must not outlive its gate. Each park is stamped with its own request id, so
 * that release names the exact request it parked and can never take a newer one
 * down with it.
 *
 * TAKING A RECORD IS DECIDED BY A LOCK, NEVER BY WHAT STORAGE SAYS. Nothing a
 * document reads in `localStorage` can prove what another document has already
 * done with it: reads are served from a per-renderer cache that another
 * document's write reaches only when the main thread gets round to applying it,
 * and a loading landing does not get round to it quickly. Measured on two
 * landings reloaded together — a removal was still invisible to the other
 * document 386ms later, and a write reached the other document's `storage`
 * event after 728ms. So absence proves only that SOMEBODY removed the value;
 * an identity written into the record proves only that nobody had overwritten
 * it as of a stale read; and — the shape that actually double-replayed — a
 * document still holding a cached copy of a record that was taken and removed
 * writes the WHOLE RECORD BACK, resurrecting it for itself and for the winner's
 * next pass. Both tabs then replayed, three runs out of three. No settle
 * constant fixes that, because the delay is bounded by nothing.
 *
 * `navigator.locks` answers the real question. It is arbitrated in the browser
 * process with no renderer cache in the path, so a document asking for the lock
 * that names a request is told immediately and truthfully whether it is the one
 * taking it. The ask is `ifAvailable`, so a loser is answered NOW rather than
 * queued behind a winner it has nothing to wait for. A winner HOLDS its lock
 * for the document's life: releasing it would make the request claimable again
 * and let a later landing replay what is already on screen. A holder that
 * crashes or is closed releases it automatically, which wedges nothing — the
 * replay went with it, so the next landing taking the record is exactly right.
 *
 * TWO LOCKS, TWO QUESTIONS. That per-request lock answers only WHO TAKES a
 * request. Every MUTATION of the stored record — a park, a dismissal's release,
 * a take's removal, the cleanup of a value that is not a current record — runs
 * inside a second lock instead, {@link RECORD_LOCK_NAME}: ONE carrier-wide name
 * for the one carrier key. It cannot be named per request, because a park is
 * minting an id nobody else knows yet and a cleanup is aimed at whatever
 * happens to be stored — under per-request names those two would run straight
 * through each other and through a take. One name makes park, release, cleanup
 * and the take's removal mutually exclusive, which is the whole point.
 *
 * That section is entered with a QUEUEING request rather than `ifAvailable`: a
 * park that arrives while a take is running must wait its turn, never be
 * dropped. Its body is wholly synchronous — no mutation holds the lock across
 * an await — and it re-reads the key AFTER the grant, the freshest read this
 * document can make, taken at the one moment no other document is mid-mutation.
 * The two locks cannot deadlock: the per-request lock is only ever asked for
 * with `ifAvailable`, so nothing ever waits on it and it can never be the tail
 * of a wait chain, and no mutation body opens a section of its own — Web Locks
 * are not re-entrant, so a nested request on the same name would wedge.
 *
 * A QUEUE MAKES EVERY MUTATION ASYNCHRONOUS, SO THE HANDOFF WAITS FOR IT. The
 * gate must not go up until the record is actually down: the landing reads the
 * carrier ONCE per auth-ready, and a park still queued behind another document's
 * mutation when that read happens is an intent lost now and replayed off an
 * unrelated sign-in later. Measured on the live app with the record lock held —
 * the gate was on screen 221ms after the click with the key still empty, a fast
 * sign-in then restored nothing, and releasing the lock wrote the record
 * afterwards, leaving it for a stranger's sign-in. So
 * `requestAuthForPostLoginIntent` awaits its park and
 * `discardOwnPostLoginIntentRequest` its withdrawal, and each raises its gate
 * INSIDE that await rather than beside it. The order therefore does not depend
 * on anyone holding the promise they return: a call already running runs to
 * completion whether or not its result is kept. Callers thread it up for as far
 * as their types can carry it; at a void-typed click prop it stops and is
 * dropped, which costs nothing, because nothing after the click needs it. Where
 * there is no lock manager the same bodies run inline, which satisfies the order
 * trivially rather than by luck.
 *
 * What the section removes is INTERLEAVING, not staleness. `localStorage` reads
 * inside it are still served from this renderer's cache; the Web Locks manager
 * arbitrates ownership and is not a cache-coherence barrier. A document whose
 * cache has not caught up can therefore still act on a record another document
 * has replaced. What can no longer happen is one document's read-decide-remove
 * being straddled by another document's write, which is the part that IS in
 * this module's gift — `localStorage` offers no conditional remove, so the rest
 * is a property of the substrate, not a knob here.
 *
 * A REFUSED PARK TAKES THE SUPERSEDED RECORD DOWN WITH IT. A park is a
 * replacement, and what makes the older request stale is the ask, not the
 * write: the user has moved on from it either way. A refused write that left it
 * standing would be the worst of both — the gate goes up with no request id to
 * arm a dismissal release with, so nothing withdraws the survivor, and the
 * sign-in that follows claims and replays it. That is the shape a user meets as
 * "I asked to build a team, it signed me in, and it deployed something else".
 * So the refused park drops what it was replacing and only then reports no
 * park, which is what makes "this ends as a plain sign-in" true rather than
 * hopeful.
 *
 * WHAT A DOUBLE REFUSAL LEAVES IS NOT PRETENDED AWAY. Storage that refuses the
 * write can refuse the removal too. Then the older record IS still standing and
 * a later landing can still claim and replay it: the answer is still no park, no
 * handle is taken for the one that never landed, and the older handle is
 * deliberately kept so this tab's next plain sign-in withdraws it on the way in.
 * Nothing here can do better against a store that refuses both operations —
 * `localStorage` is the carrier, and this module never claims a guarantee the
 * substrate is not giving it.
 *
 * REMOVAL REPORTS ITSELF. {@link removeParkedRecordIfUnchanged} removes only
 * while the stored text is still exactly the text the section read, then
 * confirms the value is gone, and a take whose removal did NOT take effect
 * returns FALSE: replaying a record still standing would leave it there for
 * another document to replay again. Nothing in this module ever writes a record
 * back, so a stale reader has nothing to resurrect. Where the platform has no
 * lock manager, the same bodies run unserialized — the strongest thing
 * `localStorage` offers by itself: exactly-once within a document, and across
 * documents only as good as how fresh their reads happen to be.
 *
 * `localStorage`, not `sessionStorage`: magic link, Google and Apple all leave
 * the origin and can come back in a DIFFERENT tab (a magic link opened from
 * webmail), and these intents need no same-tab context to resume — a deploy
 * names its team, a build starts blank, a checkout names its price. That is
 * exactly the property the tab-scoped in-team park
 * (`Cteamsgallery/gallery.copy-intent`) does NOT have: it resumes INTO a team
 * already on screen, rides the tab-scoped auth return route, and expires —
 * which is why the two carriers stay separate mechanisms.
 *
 * Reopening a TEAM's view is deliberately not carried here. It is already
 * served by the `?viewTeam=` URL grammar riding the auth return route
 * (`lib/authReturnRoute` + the landing's URL restore); a second reopen path —
 * no TTL, not tab-scoped — would race it and resurrect a stale open on some
 * later visit. Do not add one back.
 */

import { TeamCrafterMode } from "@cabal/shared/domain/team-crafter/enums.js";
import type { OpenAuthModalOptions } from "@/contexts/layout";
import { PostLoginIntentKind } from "@/domain/post-login-intent/enums";
import type {
  ParkedPostLoginIntent,
  PostLoginIntent,
} from "@/domain/post-login-intent/types";

const POST_LOGIN_INTENT_KEY = "cabal_post_login_intent";

/**
 * The request THIS TAB parked, in the one store that is scoped exactly like the
 * question it answers: `sessionStorage` dies with the tab but survives the tab's
 * reloads and its trip out to the sign-in provider and back. Module memory
 * cannot answer it — a reload empties module memory while the deliberately
 * cross-tab `localStorage` record stands, and the tab would then no longer
 * recognise its own park. Being tab-scoped, it is the one piece of carrier state
 * no other document contends, so it needs no lock of its own.
 */
const OWN_PARKED_REQUEST_KEY = "cabal_post_login_intent_own_request";

/** The lock that owns one parked request, named after that request so a fresh
 *  park is a fresh election and a taken one stays taken. Asked for with
 *  `ifAvailable`, held for the winner's whole life. */
const CLAIM_LOCK_NAME_PREFIX = "cabal_post_login_intent_claim:";

/** The lock every mutation of the stored record runs inside — one name for the
 *  one carrier key, so a park, a release, a cleanup and a take's removal are
 *  mutually exclusive whatever request each of them is about. Queued, not
 *  `ifAvailable`: a mutation waits its turn rather than being dropped. */
const RECORD_LOCK_NAME = "cabal_post_login_intent_record";

/** The fallback identity's width — the same 16 bytes the secure-context
 *  UUID carries. */
const IDENTITY_BYTE_COUNT = 16;
const HEX_RADIX = 16;
const HEX_DIGITS_PER_BYTE = 2;
const BYTE_VALUE_COUNT = 256;

/** What the lock manager said about this document's ask. `Unsupported` is not a
 *  loss — it is "this platform cannot answer", which routes to the storage-only
 *  take rather than silently declining a replay nobody else is doing. */
enum ClaimLockOutcome {
  Granted = "granted",
  Denied = "denied",
  Unsupported = "unsupported",
}

/** What the carrier key holds as this document can currently see it. Three
 *  states rather than a nullable record, because the mutations have to tell
 *  "nothing here" apart from "something here that is not a record" — the first
 *  is nothing to do, the second is the cleanup's whole job. */
enum ParkedRecordState {
  Empty = "empty",
  Invalid = "invalid",
  Present = "present",
}

function readLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Resuming an
    // intent is a convenience — never let it break the sign-in path.
    return null;
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // See readLocalStorage.
  }
}

function removeLocalStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // See readLocalStorage.
  }
}

function readSessionStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // See readLocalStorage.
    return null;
  }
}

function writeSessionStorage(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // See readLocalStorage.
  }
}

function removeSessionStorage(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // See readLocalStorage.
  }
}

/** The Web Crypto API, or undefined where the environment has none. Typed
 *  optional on purpose: the DOM lib declares `crypto` as always present, which
 *  is not true of every runtime this bundle is evaluated in. */
function getWebCrypto(): Crypto | undefined {
  return globalThis.crypto;
}

/** The Web Locks manager, or undefined where the environment has none. Typed
 *  optional for the same reason as {@link getWebCrypto}, and with a live reason
 *  behind it: Web Locks needs a secure context, so an insecure origin and some
 *  embedded webviews have no manager at all. */
function getLockManager(): LockManager | undefined {
  return globalThis.navigator.locks;
}

function createRandomIdentityBytes(): Uint8Array {
  const webCrypto = getWebCrypto();
  if (typeof webCrypto?.getRandomValues === "function") {
    return webCrypto.getRandomValues(new Uint8Array(IDENTITY_BYTE_COUNT));
  }
  return Uint8Array.from({ length: IDENTITY_BYTE_COUNT }, () =>
    Math.floor(Math.random() * BYTE_VALUE_COUNT),
  );
}

/**
 * A fresh identity for ONE park. `crypto.randomUUID` needs a secure context, so
 * an insecure origin falls back to random bytes and an environment without Web
 * Crypto to `Math.random`: these ids only have to tell one person's concurrent
 * parks apart, and minting one must never throw on the sign-in path.
 */
function createRandomIdentity(): string {
  const webCrypto = getWebCrypto();
  if (typeof webCrypto?.randomUUID === "function") {
    return webCrypto.randomUUID();
  }
  return Array.from(createRandomIdentityBytes(), (randomByte) =>
    randomByte.toString(HEX_RADIX).padStart(HEX_DIGITS_PER_BYTE, "0"),
  ).join("");
}

function isPostLoginIntentKind(value: string): value is PostLoginIntentKind {
  return (Object.values(PostLoginIntentKind) as string[]).includes(value);
}

function isTeamCrafterMode(value: unknown): value is TeamCrafterMode {
  return (
    typeof value === "string" &&
    (Object.values(TeamCrafterMode) as string[]).includes(value)
  );
}

/** A parked record together with the exact stored text it was read from. That
 *  text is what a removal compares against, so the record a caller validated
 *  and the record it drops is provably the same one. */
interface ParkedPostLoginIntentRecord extends ParkedPostLoginIntent {
  raw: string;
}

type ParkedRecordReading =
  | { state: ParkedRecordState.Empty }
  | { state: ParkedRecordState.Invalid; raw: string }
  | { state: ParkedRecordState.Present; record: ParkedPostLoginIntentRecord };

/**
 * Every claim this document has already DECIDED, by request id — kept for the
 * document's life rather than cleared when it settles. Two things need that.
 * The landing's restore effect re-runs whenever its auth inputs settle, and a
 * re-run must be handed the answer already given rather than open a second
 * claim against the same record. And a granted lock is held by THIS document,
 * so asking for it again reports "unavailable" — a winner re-asking would read
 * its own hold as a loss and drop the replay on the floor. Both callers get the
 * one answer; only the one that is still live replays it.
 */
const decidedClaims = new Map<string, Promise<boolean>>();

type PostLoginIntentCandidate = Partial<
  Record<"kind" | "teamId" | "paddlePriceId" | "mode", unknown>
>;

type ParkedPostLoginIntentCandidate = Partial<
  Record<"requestId" | "intent", unknown>
>;

/** The tab-scoped handle on this tab's own park, mirrored in module memory so
 *  the common "nothing parked here" answer costs no storage read. Seeded from
 *  `sessionStorage` on first use, which is the read that survives the reload
 *  and the sign-in round trip. */
let ownParkedRequestId: string | null = null;
let hasSeededOwnParkedRequestId = false;

function readOwnParkedRequestId(): string | null {
  if (hasSeededOwnParkedRequestId) return ownParkedRequestId;
  hasSeededOwnParkedRequestId = true;
  ownParkedRequestId = readSessionStorage(OWN_PARKED_REQUEST_KEY);
  return ownParkedRequestId;
}

function rememberOwnParkedRequestId(requestId: string): void {
  ownParkedRequestId = requestId;
  hasSeededOwnParkedRequestId = true;
  writeSessionStorage(OWN_PARKED_REQUEST_KEY, requestId);
}

/** Drop this tab's handle once the request it names is gone — claimed, released
 *  or replaced. A handle aimed at a record that is no longer the one this tab
 *  parked would let a later plain sign-in discard a stranger's request. */
function forgetOwnParkedRequestId(requestId: string): void {
  if (readOwnParkedRequestId() !== requestId) return;
  ownParkedRequestId = null;
  hasSeededOwnParkedRequestId = true;
  removeSessionStorage(OWN_PARKED_REQUEST_KEY);
}

function parsePostLoginIntent(value: unknown): PostLoginIntent | null {
  if (typeof value !== "object" || value == null) return null;
  const candidate = value as PostLoginIntentCandidate;
  if (typeof candidate.kind !== "string" || !isPostLoginIntentKind(candidate.kind)) {
    return null;
  }
  const parserByKind: Record<PostLoginIntentKind, () => PostLoginIntent | null> = {
    [PostLoginIntentKind.TeamModalDeploy]: () =>
      typeof candidate.teamId === "string" && candidate.teamId.length > 0
        ? { kind: PostLoginIntentKind.TeamModalDeploy, teamId: candidate.teamId }
        : null,
    [PostLoginIntentKind.TeamModalNewTeam]: () => ({
      kind: PostLoginIntentKind.TeamModalNewTeam,
    }),
    [PostLoginIntentKind.PlanCheckout]: () =>
      typeof candidate.paddlePriceId === "string" &&
      candidate.paddlePriceId.length > 0
        ? {
            kind: PostLoginIntentKind.PlanCheckout,
            paddlePriceId: candidate.paddlePriceId,
          }
        : null,
    // Create carries no team; edit names the team it works on. A team id that
    // is present but empty is a malformed park, not a create.
    [PostLoginIntentKind.TeamCrafter]: () => {
      if (!isTeamCrafterMode(candidate.mode)) return null;
      if (candidate.teamId === undefined) {
        return { kind: PostLoginIntentKind.TeamCrafter, mode: candidate.mode };
      }
      return typeof candidate.teamId === "string" && candidate.teamId.length > 0
        ? {
            kind: PostLoginIntentKind.TeamCrafter,
            mode: candidate.mode,
            teamId: candidate.teamId,
          }
        : null;
    },
  };
  return parserByKind[candidate.kind]();
}

/** A stored value that is not a CURRENT parked record — malformed JSON, a
 *  half-written payload, or anything written before request identity existed —
 *  parses as null and reads as {@link ParkedRecordState.Invalid}. There is no
 *  compat branch by design: a park no dismissal can release exactly, and no
 *  claim can name, is a park nothing should replay. */
function parseParkedPostLoginIntent(raw: string): ParkedPostLoginIntent | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed == null) return null;
  const candidate = parsed as ParkedPostLoginIntentCandidate;
  if (typeof candidate.requestId !== "string" || candidate.requestId.length === 0) {
    return null;
  }
  const intent = parsePostLoginIntent(candidate.intent);
  if (intent == null) return null;
  return { requestId: candidate.requestId, intent };
}

/** The carrier's ONE serializer, so a park and the text every later read
 *  compares against are the same text for the same record. */
function serializeParkedRecord({
  requestId,
  intent,
}: ParkedPostLoginIntent): string {
  return JSON.stringify({ requestId, intent });
}

/**
 * Drop the stored value while it is still exactly the text the caller read,
 * then confirm it is gone. Ownership is NOT what this reports — the claim lock
 * decides that before anyone removes anything — only whether the exact record
 * the caller validated was actually taken down: a value that no longer matches
 * fails the comparison and is left alone, and a removal storage refused fails
 * the confirmation. Its callers report that answer verbatim, so nothing acts on
 * a record that is still standing.
 */
function removeParkedRecordIfUnchanged(expectedRaw: string): boolean {
  if (readLocalStorage(POST_LOGIN_INTENT_KEY) !== expectedRaw) return false;
  removeLocalStorage(POST_LOGIN_INTENT_KEY);
  return readLocalStorage(POST_LOGIN_INTENT_KEY) !== expectedRaw;
}

/** What the carrier key holds right now, with the exact text behind it. Pure:
 *  reading never mutates, so it is equally safe inside a record section and in
 *  the landing's peek. */
function readParkedRecord(): ParkedRecordReading {
  const raw = readLocalStorage(POST_LOGIN_INTENT_KEY);
  if (raw == null) return { state: ParkedRecordState.Empty };
  const parked = parseParkedPostLoginIntent(raw);
  if (parked == null) return { state: ParkedRecordState.Invalid, raw };
  return {
    state: ParkedRecordState.Present,
    record: { raw, requestId: parked.requestId, intent: parked.intent },
  };
}

/**
 * Run ONE mutation of the stored record inside the carrier-wide record lock and
 * report whether it took effect. This is the only way any code here writes to
 * or removes the carrier key, so every mutation is serialized against every
 * other one whatever request it is about.
 *
 * The body must be synchronous and must not open a section of its own: Web
 * Locks are not re-entrant, and a lock held across an await is a lock that can
 * be held across a hang. Because every storage access inside these bodies is
 * already guarded, a body cannot throw — so a rejected `request` means the
 * manager refused the section rather than the body failing, and running it
 * unserialized is then the same best effort as having no manager at all.
 */
async function runRecordMutation(mutate: () => boolean): Promise<boolean> {
  const lockManager = getLockManager();
  if (lockManager == null) return mutate();
  const completed: boolean[] = [];
  await lockManager
    .request(RECORD_LOCK_NAME, (): void => {
      completed.push(mutate());
    })
    .catch((lockError: unknown): unknown => lockError);
  const [completedResult] = completed;
  if (typeof completedResult === "boolean") return completedResult;
  return mutate();
}

/**
 * IN-SECTION ONLY. Write one parked record over whatever the key holds — a
 * second park replaces the first, because there is only ever one pending
 * interruption — and confirm the text that landed is the text intended, so a
 * park storage refused is reported as no park.
 *
 * The tab's handle on that request is taken here, inside the same section and
 * only on that confirmation. It is one park, not two steps: the handle is what a
 * later plain navbar sign-in withdraws, so one naming a record that was never
 * written would put this tab's PREVIOUS park — a record that does still stand —
 * out of its own reach.
 *
 * A REFUSED WRITE STILL SUPERSEDES. What the user just asked for outranks
 * whatever was standing, and a storage refusal does not make the standing
 * request wanted again. Leaving it is the worst of the three outcomes: the
 * refused park raises a gate with NO dismissal release, so the survivor is
 * claimed and replayed by the very sign-in that follows — the user asked for one
 * thing, was told nothing was parked, and is handed a different intent. So the
 * refusal drops the superseded record before reporting FALSE. What a drop that
 * is ALSO refused leaves behind is in the module header, stated rather than
 * papered over.
 */
function parkRecordForThisTab(parked: ParkedPostLoginIntent): boolean {
  const raw = serializeParkedRecord(parked);
  writeLocalStorage(POST_LOGIN_INTENT_KEY, raw);
  if (readLocalStorage(POST_LOGIN_INTENT_KEY) !== raw) {
    // The drop reports whether the superseded record actually went down, and
    // that answer is not this park's to return: the park failed either way, and
    // reporting a drop as a park is exactly the lie that would hand the session
    // an intent nobody asked for. The module header is what carries it instead.
    dropSupersededParkedRecord();
    return false;
  }
  rememberOwnParkedRequestId(parked.requestId);
  return true;
}

/**
 * IN-SECTION ONLY. Drop the record stored under THIS identity, and let go of
 * this tab's handle on it once it is gone. One body for both ways a request
 * ends — the winner's take and the parker's withdrawal — because they differ
 * only in what entitles the caller to make the call, never in what happens to
 * the record.
 *
 * The record is read here, after the grant, rather than passed in: it is the
 * freshest read this document can make and the one moment no other document is
 * mid-mutation. Nothing stored, garbage stored, or a different id stored all
 * mean this request is gone — the handle goes with it and the caller is told it
 * took nothing. A removal that did not take effect leaves the handle in place,
 * so a later discard can still withdraw the record that is still standing.
 */
function dropParkedRequest(requestId: string): boolean {
  const reading = readParkedRecord();
  if (
    reading.state !== ParkedRecordState.Present ||
    reading.record.requestId !== requestId
  ) {
    forgetOwnParkedRequestId(requestId);
    return false;
  }
  const dropped = removeParkedRecordIfUnchanged(reading.record.raw);
  if (dropped) forgetOwnParkedRequestId(requestId);
  return dropped;
}

/**
 * IN-SECTION ONLY. Drop a stored value that is not a current parked record. The
 * value is re-read here rather than carried in from the reader that noticed it,
 * so a valid park written since — by this tab or another — is what this sees and
 * is left untouched.
 */
function dropInvalidParkedRecord(): boolean {
  const reading = readParkedRecord();
  if (reading.state !== ParkedRecordState.Invalid) return false;
  return removeParkedRecordIfUnchanged(reading.raw);
}

/**
 * IN-SECTION ONLY. Take down the record a park was about to replace, for a park
 * whose own write storage refused. The replacement is what the user asked for,
 * so the standing request is superseded by the ask rather than by the write:
 * completing the destructive half of a replacement whose write never landed is
 * what makes that refused park end as the plain sign-in this module says it is.
 *
 * It is the SAME body a take and a withdrawal use, reached with the id the
 * standing record itself carries — so there is one guarded compare-and-remove
 * here, not a second removal path, and this tab's handle is let go exactly when
 * the record dropped was this tab's own. That body re-reads under the grant by
 * design; the read here only names which request is standing.
 *
 * A stored value that is not a current record is left alone: nothing can replay
 * it, and clearing it belongs to the reader that meets it
 * ({@link readPostLoginIntentRequest}). TRUE therefore means no replayable
 * record stands — dropped, or never there. FALSE means one is still standing,
 * which is a state this module reports rather than hides; see the module header.
 */
function dropSupersededParkedRecord(): boolean {
  const reading = readParkedRecord();
  if (reading.state !== ParkedRecordState.Present) return true;
  return dropParkedRequest(reading.record.requestId);
}

/** Park one intent immediately before handing off to sign-in, under a fresh
 *  request identity, and hand that identity back to the gate that must release
 *  it. Module private on purpose: parking WITHOUT arming the gate's dismissal
 *  release is the bug this carrier had, so `requestAuthForPostLoginIntent` is
 *  the only way in, and the id is minted HERE so no caller can park without
 *  one.
 *
 *  RESOLVES ONLY ONCE THE RECORD IS DOWN, so the identity a gate is armed with
 *  always names a record that exists. The write runs in the queueing record
 *  section, which can be behind another document's mutation for as long as that
 *  mutation takes; handing the id back before it ran would raise a gate over an
 *  empty carrier. A write storage refused resolves to NULL — no record, no
 *  identity, and no handle for this tab either, since nothing was parked, and
 *  the record this park was replacing is taken down inside the same section, so
 *  the gate that follows has nothing left to replay. */
async function parkPostLoginIntent(
  intent: PostLoginIntent,
): Promise<string | null> {
  const requestId = createRandomIdentity();
  const parked = await runRecordMutation((): boolean =>
    parkRecordForThisTab({ requestId, intent }),
  );
  return parked ? requestId : null;
}

/**
 * Release the request parked under THIS identity — the way a request is
 * withdrawn rather than taken. The gate's dismissal and this tab's own discard
 * both mean "forget what I parked". A record carrying a different request id has
 * already been replaced by a newer park, which is not this caller's to remove,
 * and the record lock is what keeps this withdrawal from cutting across a park,
 * a cleanup or a take that is already in flight.
 */
function releaseParkedRequest(requestId: string): Promise<boolean> {
  return runRecordMutation((): boolean => dropParkedRequest(requestId));
}

/** What a granted claim hands back to its lock callback: a promise that never
 *  settles, so the lock stays held until this document goes away. Deliberate,
 *  and the reason the record lock has to be a SECOND name: this one is never
 *  given back by hand, so no mutation could ever run under it. */
function holdForDocumentLife(): Promise<never> {
  return new Promise<never>(() => undefined);
}

/**
 * Ask the browser process whether THIS document takes the request — the one
 * question no read of `localStorage` can answer.
 *
 * `ifAvailable` is what makes the answer immediate for both sides: the lock is
 * held for the winner's whole life, so a queued request would never come back,
 * and a loser needs nothing from the winner anyway — "not mine" is a complete
 * answer. It is also what makes the two-lock scheme deadlock-free: nothing ever
 * waits on this lock, so it can never be the tail of a wait chain while its
 * holder waits for the record lock. A rejection from the manager is reported as
 * `Unsupported` rather than a loss: refusing to answer is not the same as
 * another document holding the request, and treating it as one would drop a
 * replay nobody is doing.
 */
function requestClaimLock(requestId: string): Promise<ClaimLockOutcome> {
  const lockManager = getLockManager();
  if (lockManager == null) {
    return Promise.resolve(ClaimLockOutcome.Unsupported);
  }
  return new Promise<ClaimLockOutcome>((resolveOutcome) => {
    void lockManager
      .request(
        `${CLAIM_LOCK_NAME_PREFIX}${requestId}`,
        { ifAvailable: true },
        (lock: Lock | null): Promise<never> | undefined => {
          if (lock == null) {
            resolveOutcome(ClaimLockOutcome.Denied);
            return undefined;
          }
          resolveOutcome(ClaimLockOutcome.Granted);
          return holdForDocumentLife();
        },
      )
      .catch((lockError: unknown) => {
        resolveOutcome(ClaimLockOutcome.Unsupported);
        return lockError;
      });
  });
}

/** One claim over the record parked under `requestId`: settle who takes it on
 *  the lock that names it, and only then enter the record section that drops
 *  it. The two takes are the SAME mutation — what differs is what entitled this
 *  document to attempt it, and how much the platform could serialize it. */
async function runClaim(requestId: string): Promise<boolean> {
  const outcome = await requestClaimLock(requestId);
  const takeByOutcome: Record<ClaimLockOutcome, () => Promise<boolean>> = {
    // This document holds the lock that owns the request, so it is the one
    // allowed to drop the record — and it does so in the record section, which
    // is where every other mutation runs too.
    [ClaimLockOutcome.Granted]: () =>
      runRecordMutation((): boolean => dropParkedRequest(requestId)),
    // Another document holds it. Nothing to remove and nothing to replay.
    [ClaimLockOutcome.Denied]: () => Promise.resolve(false),
    // No manager to ask, so the guarded removal IS the claim: exactly-once
    // within this document, and across documents only as good as how fresh
    // their reads happen to be. That is the honest ceiling here, not a
    // guarantee — it is the guarantee Web Locks exists to provide.
    [ClaimLockOutcome.Unsupported]: () =>
      runRecordMutation((): boolean => dropParkedRequest(requestId)),
  };
  return takeByOutcome[outcome]();
}

/**
 * The parked request — its intent AND the identity a claim needs. Reading does
 * NOT consume and does not mutate at all: the reader takes the record by
 * claiming that identity, and acts only if the claim reports success. A stored
 * value that is not a current record reads as nothing, so garbage can never
 * wedge the restore into re-reading it forever; dropping it is a mutation, so it
 * is handed to the record section, which re-reads under the lock and can
 * therefore never take down a valid park written since.
 */
export function readPostLoginIntentRequest(): ParkedPostLoginIntent | null {
  const reading = readParkedRecord();
  if (reading.state === ParkedRecordState.Invalid) {
    void runRecordMutation(dropInvalidParkedRecord);
    return null;
  }
  if (reading.state === ParkedRecordState.Empty) return null;
  return { requestId: reading.record.requestId, intent: reading.record.intent };
}

/**
 * Take the request parked under THIS identity, and only it. The comparison is
 * the request id, never the payload: this carrier is cross-tab
 * `localStorage`, so two tabs can park byte-identical intents (the same Build,
 * the same team's Deploy, the same price's checkout), and a payload match would
 * let one tab's abandoned gate delete the other tab's live request. An id that
 * does not match what is stored means a newer park has already replaced this
 * one — a no-op, as is a claim with nothing parked.
 *
 * TRUE means this document holds the lock that owns that request AND the record
 * was actually removed, which makes it the only caller allowed to act on it:
 * two landings reading the same park both ask, the browser process grants
 * exactly one, and the loser is told so before anything is on screen. FALSE
 * means this document must not act — it lost the lock, the record was replaced
 * by a newer park, or the removal did not take effect and the record is still
 * standing for someone else to take. The answer is memoized per request for this
 * document's life — see {@link decidedClaims}.
 */
export function claimPostLoginIntent(requestId: string): Promise<boolean> {
  const decided = decidedClaims.get(requestId);
  if (decided != null) return decided;
  const claim = runClaim(requestId);
  decidedClaims.set(requestId, claim);
  return claim;
}

/**
 * Discard the request THIS TAB parked, if it still stands. A plain navbar
 * sign-in carries no intent, so this tab's own pending one is stale — but the
 * carrier is shared, and another tab's live request is not this tab's to
 * delete. A tab holding no parked identity discards nothing. The identity is
 * tab-scoped storage rather than module memory precisely so that a reload
 * between the park and the sign-in cannot make this tab a stranger to its own
 * request.
 *
 * The withdrawal runs in the record section, so it lands as a whole between
 * other documents' mutations — and its caller AWAITS it before raising the
 * plain gate, for the mirror of the reason a park is awaited: returning while
 * the withdrawal is still queued would let a session land over a request this
 * tab has already decided is stale, and the landing would replay it. TRUE means
 * this tab's record was taken down; FALSE means this tab had nothing parked, or
 * the removal did not take effect and the record still stands — in which case
 * the handle is deliberately kept, so a later discard can try again.
 */
export function discardOwnPostLoginIntentRequest(): Promise<boolean> {
  const ownRequestId = readOwnParkedRequestId();
  if (ownRequestId == null) return Promise.resolve(false);
  return releaseParkedRequest(ownRequestId);
}

/**
 * The ONE gate-before-open handoff: park what the user asked for, raise the
 * sign-in gate over a record that is already down, and release the intent again
 * if they dismiss the gate instead of signing in. Without that release the
 * intent outlives the abandoned request — this carrier is `localStorage` — and
 * the landing's replay would later execute it off an unrelated sign-in.
 * `onDismiss` fires ONLY on a close without a session (a landed session clears
 * the callback silently), which is exactly the path that must forget — and it
 * forgets THIS request by releasing its own identity, so a request parked
 * afterwards (by this tab or another) stands. Nothing waits on that release:
 * the gate is going away, and a take racing it queues behind it on the one
 * record lock, so the withdrawal still wins the record it named.
 *
 * THE GATE IS RAISED AFTER THE PARK, NEVER BESIDE IT — the whole reason this is
 * asynchronous, and why callers await it. See the module header.
 *
 * A PARK THAT DID NOT LAND STILL RAISES THE GATE: the user asked to sign in, and
 * that is not this carrier's to refuse over a convenience it could not deliver.
 * It is raised WITHOUT a dismissal release, because there is no record to
 * withdraw, and the answer is FALSE so no caller believes an intent is waiting
 * for the session. What makes that a plain sign-in rather than a trap is the
 * park itself: a refused write takes the record it was replacing down with it,
 * so the gate this opens has nothing standing behind it to claim. Where storage
 * refuses that removal as well, the older record survives and this can still
 * hand it to the session — the one case the header states outright, because it
 * is a store refusing twice, not a promise this module is breaking.
 */
export async function requestAuthForPostLoginIntent(input: {
  intent: PostLoginIntent;
  openAuthModal: (options?: OpenAuthModalOptions) => void;
}): Promise<boolean> {
  const requestId = await parkPostLoginIntent(input.intent);
  if (requestId == null) {
    input.openAuthModal();
    return false;
  }
  input.openAuthModal({
    onDismiss: () => {
      void releaseParkedRequest(requestId);
    },
  });
  return true;
}
